import { Box, Rating, Stack, Typography } from '@mui/material';
import { AppGlobalClassKeys } from 'app/appGlobalStyles';
import { MprcContainer, MprcJoinComponents, MprcLink } from 'components';
import { ClassroomViewDto, EnrolledUsersOption, ITrainingViewDto, TrainingViewDto } from 'generated/CoreApi_v1';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ITraininRegistrationProps } from 'views/lms/student/Registration';
import { RegistrationDefaultHighlights, RegistrationResumeHighlights } from 'views/lms/student/Registration/components/RegistrationDefaultHighlights';
import { RegistrationMultipleClassesHighlights } from 'views/lms/student/Registration/components/RegistrationMultipleClassesHighlights';
import { TrainingListUsersDrawer } from 'views/lms/training/components/TrainingListUsersDrawer';

///////////////////////////////////////////////////////////////////////////////

interface IProps extends ITraininRegistrationProps {
    training: TrainingViewDto | null;
    classrooms: ClassroomViewDto[];
    selectedClassroom: number;
    readonly?: boolean;
    showHighlights: "full" | "resume";
    onChangeTab: (tab: number) => void;
}

///
export const RegistrationGeneralView = ({ training, classrooms, selectedClassroom, readonly, showHighlights = "full", onChangeTab }: IProps) => {
    const { t } = useTranslation("lms");
    const [dialog, setDialog] = useState<any>();

    //;
    const tvData = useMemo(() => {
        const tvData: ITrainingViewDto = selectedClassroom >= 0
            ? classrooms[selectedClassroom]
            : training!;
        return tvData;
    }, [training, classrooms, selectedClassroom]);

    ///
    const handleViewEnrollments = (option: EnrolledUsersOption) => {
        setDialog(
            <TrainingListUsersDrawer
                option={option}
                trainingId={training!.trainingId}
                title={option == EnrolledUsersOption.Enrolled
                    ? t("Registration.Training.ViewFinishedEnrollsTitle")
                    : t("Registration.Training.ViewActiveEnrollsTitle")}
                subtitle={training?.name}
                onClose={() => setDialog(null)}
            />
        );
    };

    return training == null
        ? null
        : (
            <Box id="generalView">
                {dialog}
                <MprcContainer>
                    <Stack direction="row" alignItems="center" spacing={1} fontSize="12px">
                        <MprcJoinComponents
                            components={[
                                training.ratingCount > 0
                                    ? <Stack direction="row" alignItems="center">
                                        <Rating name="read-only" value={training.ratingAvg} readOnly precision={0.5} />
                                        <MprcLink
                                            fontSize="inherit"
                                            variant="linkAction"
                                            onClick={() => onChangeTab(3)}
                                        >
                                            <span>(</span>
                                            {t("Training.Literals.StarRatingLong", { count: training.ratingCount })}
                                            <span>)</span>
                                        </MprcLink>
                                    </Stack>
                                    : null
                                , training.usersEnrolledCount > 0
                                    ? <div>
                                        {training.enableViewEnrolledUsers
                                            ? <MprcLink
                                                variant="linkAction"
                                                fontSize="inherit"
                                                onClick={() => handleViewEnrollments(EnrolledUsersOption.Enrolled)}
                                            >
                                                {t("Training.Literals.EnrollmentsLong", { count: training.usersEnrolledCount })}
                                            </MprcLink>
                                            : <Typography variant="caption" component="span">{t("Training.Literals.EnrollmentsLong", { count: training.usersEnrolledCount })}</Typography>
                                        }
                                        <span>&nbsp;</span>
                                        {t("Training.Literals.EnrollmentLongComplement", { count: training.usersEnrolledCount })}
                                    </div>
                                    : null
                                , training.activeEnrollmentsCount > 0
                                    ? <div>
                                        {training.enableViewEnrolledUsers
                                            ? <MprcLink
                                                variant="linkAction"
                                                fontSize="inherit"
                                                onClick={() => handleViewEnrollments(EnrolledUsersOption.Active)}
                                            >
                                                {t("Training.Literals.EnrollmentsLong", { count: training.activeEnrollmentsCount })}
                                            </MprcLink>
                                            :
                                            <Typography variant="caption" component="span">{t("Training.Literals.EnrollmentsLong", { count: training.activeEnrollmentsCount })}</Typography>
                                        }
                                        <span>&nbsp;</span>
                                        {t("Training.Literals.ActiveEnrollmentLongComplement")}
                                    </div>
                                    : null
                            ]}
                            separator={<span>&bull;</span>}
                        />
                    </Stack>
                </MprcContainer>

                {
                    showHighlights == "full" &&
                    <Box
                        sx={{ mb: 3 }}
                        overflow="auto"
                        className={AppGlobalClassKeys.thinScrollbar}
                    >
                        {
                            classrooms.length > 1
                                ? <RegistrationMultipleClassesHighlights trainingView={tvData} onClick={() => onChangeTab(1)} />
                                : <RegistrationDefaultHighlights {...tvData} />
                        }
                    </Box>
                }

                {
                    showHighlights == "resume" &&
                    <RegistrationResumeHighlights {...training} />
                }
                {
                    training && training.multipleClasses == true && classrooms.length == 1 &&
                    <MprcContainer>
                        <Typography variant="h5" color="black">
                            {t("Training.Literals.Classroom")}
                        </Typography>
                        <Typography variant="subtitle1" color="black">
                            {classrooms[0].name}
                        </Typography>
                    </MprcContainer>
                }
                {
                    training.description &&
                    <MprcContainer>
                        <Typography variant="h5" color="black">
                            {t("Training.Literals.DescriptionLabel")}
                        </Typography>
                        <Typography variant="subtitle1" color="black">
                            {training.description}
                        </Typography>
                    </MprcContainer>
                }

                {
                    training.objectives &&
                    <MprcContainer>
                        <Typography variant="h5" color="black">
                            {t("Training.Literals.ObjectivesLabel")}
                        </Typography>
                        <Typography variant="subtitle1" color="black">
                            <ul className={AppGlobalClassKeys.unorderedListFlat}>
                                {training.objectives?.split("\n").map((s, i) => <li key={i}>{s}</li>)}
                            </ul>
                        </Typography>
                    </MprcContainer>
                }
            </Box>
        );
};
